import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Label
} from 'recharts';

function formatDateLabel(dateStr) {
    const date = new Date(dateStr);
    const day = date.getDate(); // e.g., 7
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()]; // e.g., Aug
    const year = date.getFullYear().toString().slice(-2); // e.g., 25
    return `${day} ${month} ${year}`; // "7 Aug 25"
}

// Transform data to use percentages based on individual metric maximums
function transformDataToRelative(data) {
    // Calculate maximum values for each metric across all data points
    const maxTestScenarios = Math.max(...data.map(item => item.test_scenarios_count));
    const maxTestPlans = Math.max(...data.map(item => item.test_plans_count));
    const maxTestSuites = Math.max(...data.map(item => item.test_suites_count));
    const maxPassedBuilds = Math.max(...data.map(item => item.passed_test_scenario_builds_count));
    const maxFailedBuilds = Math.max(...data.map(item => item.failed_test_scenario_builds_count));
    const maxResponseTime = Math.max(...data.map(item => item.average_api_response_time || 0));

    return data.map(item => {
        return {
            ...item,
            test_scenarios_percentage: maxTestScenarios > 0 ? (item.test_scenarios_count / maxTestScenarios) * 100 : 0,
            test_plans_percentage: maxTestPlans > 0 ? (item.test_plans_count / maxTestPlans) * 100 : 0,
            test_suites_percentage: maxTestSuites > 0 ? (item.test_suites_count / maxTestSuites) * 100 : 0,
            passed_builds_percentage: maxPassedBuilds > 0 ? (item.passed_test_scenario_builds_count / maxPassedBuilds) * 100 : 0,
            failed_builds_percentage: maxFailedBuilds > 0 ? (item.failed_test_scenario_builds_count / maxFailedBuilds) * 100 : 0,
            response_time_percentage: maxResponseTime > 0 ? ((item.average_api_response_time || 0) / maxResponseTime) * 100 : 0,
            // Keep original values and max values for tooltip
            original_test_scenarios: item.test_scenarios_count,
            original_test_plans: item.test_plans_count,
            original_test_suites: item.test_suites_count,
            original_passed_builds: item.passed_test_scenario_builds_count,
            original_failed_builds: item.failed_test_scenario_builds_count,
            original_response_time: item.average_api_response_time,
            max_test_scenarios: maxTestScenarios,
            max_test_plans: maxTestPlans,
            max_test_suites: maxTestSuites,
            max_passed_builds: maxPassedBuilds,
            max_failed_builds: maxFailedBuilds,
            max_response_time: maxResponseTime
        };
    });
}

// Custom tooltip to show both percentage and original values
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                backgroundColor: "var(--background-secondary)",
                border: "1px solid var(--borderColor)",
                borderRadius: "8px",
                padding: "10px",
                color: "var(--text-primary)"
            }}>
                <p style={{ margin: "0 0 5px 0", fontWeight: "bold" }}>{formatDateLabel(label)}</p>
                {payload.map((entry, index) => {
                    const dataKey = entry.dataKey;

                    let originalValue = 0;
                    let percentage = entry.value;
                    let maxValue = 0;

                    // Map dataKey to original value and max value
                    if (dataKey === 'test_scenarios_percentage') {
                        originalValue = entry.payload.original_test_scenarios;
                        maxValue = entry.payload.max_test_scenarios;
                    } else if (dataKey === 'test_plans_percentage') {
                        originalValue = entry.payload.original_test_plans;
                        maxValue = entry.payload.max_test_plans;
                    } else if (dataKey === 'test_suites_percentage') {
                        originalValue = entry.payload.original_test_suites;
                        maxValue = entry.payload.max_test_suites;
                    } else if (dataKey === 'passed_builds_percentage') {
                        originalValue = entry.payload.original_passed_builds;
                        maxValue = entry.payload.max_passed_builds;
                    } else if (dataKey === 'failed_builds_percentage') {
                        originalValue = entry.payload.original_failed_builds;
                        maxValue = entry.payload.max_failed_builds;
                    } else if (dataKey === 'response_time_percentage') {
                        return null;
                    }

                    if (originalValue === null || originalValue === undefined) {
                        return null;
                    }

                    return (
                        <p key={index} style={{ margin: "2px 0", color: entry.color }}>
                            {entry.name}: {originalValue}
                        </p>
                    );
                })}
                {payload.find(p => p.dataKey === 'response_time_percentage') && 
                 payload.find(p => p.dataKey === 'response_time_percentage')?.payload.original_response_time !== null && 
                 payload.find(p => p.dataKey === 'response_time_percentage')?.payload.original_response_time !== undefined && (
                    <p style={{ margin: "2px 0", color: "#0ea5e9" }}>
                        Response Time: {payload.find(p => p.dataKey === 'response_time_percentage')?.payload.original_response_time}/ms
                    </p>
                )}
            </div>
        );
    }
    return null;
};

export default function DailyStatsGraph({ data }) {
    const transformedData = transformDataToRelative(data);

    return (
        <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={transformedData} margin={{ top: 20, right: 40, bottom: 20, left: 20 }}>
                <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.05} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--borderColor)" />
                <XAxis
                    dataKey="date"
                    tick={{ fill: "var(--text-primary)" }}
                    interval={0}
                    tickFormatter={formatDateLabel} />
                <YAxis
                    yAxisId="left"
                    tick={{ fill: "var(--text-primary)" }}
                    label={<Label value="Percentage" angle={-90} position="insideLeft" fill="var(--text-primary)" />}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ color: "var(--text-primary)", paddingTop: "10px" }}
                    iconType="rect"
                    iconSize={12}
                />
                {/* Area chart for response time percentage */}
                <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="response_time_percentage"
                    stroke="#0ea5e9"
                    fill="url(#areaGradient)"
                    name="Response Time"
                    connectNulls
                />
                {/* Bars using percentage values */}
                <Bar yAxisId="left" dataKey="test_scenarios_percentage" fill="#bf5700" name="Test Scenarios" />
                <Bar yAxisId="left" dataKey="test_plans_percentage" fill="#624dc4" name="Test Plans" />
                <Bar yAxisId="left" dataKey="test_suites_percentage" fill="#c026d3" name="Test Suites" />
                <Bar yAxisId="left" dataKey="passed_builds_percentage" fill="#22c55e" name="Passed Builds" />
                <Bar yAxisId="left" dataKey="failed_builds_percentage" fill="#ef4444" name="Failed Builds" />
            </ComposedChart>
        </ResponsiveContainer>
    );
}
